import * as React from 'react';
import { useThemeProps } from '@mui/material/styles';
import type { DefaultizedProps } from '@mui/x-internals/types';
import type { WithDataAttributes } from '@mui/utils/types';
import type { DateOrTimeView, DateTimeValidationError } from '../models';
import { usePickerAdapter } from '../hooks/usePickerAdapter';
import type {
  DateCalendarSlots,
  DateCalendarSlotProps,
  ExportedDateCalendarProps,
} from '../DateCalendar/DateCalendar.types';
import type { BasePickerInputProps } from '../internals/models/props/basePickerProps';
import type { DateTimePickerTabsProps } from './DateTimePickerTabs';
import { DateTimePickerTabs } from './DateTimePickerTabs';
import type {
  LocalizedComponent,
  PickersInputLocaleText,
} from '../locales/utils/pickersLocaleTextApi';
import type {
  DateTimePickerToolbarProps,
  ExportedDateTimePickerToolbarProps,
} from './DateTimePickerToolbar';
import { DateTimePickerToolbar } from './DateTimePickerToolbar';
import type { PickerViewRendererLookup } from '../internals/hooks/usePicker';
import type { DateViewRendererProps } from '../dateViewRenderers';
import type { TimeViewRendererProps } from '../timeViewRenderers';
import { applyDefaultViewProps } from '../internals/utils/views';
import type { BaseClockProps, DigitalTimePickerProps } from '../internals/models/props/time';
import type {
  DateOrTimeViewWithMeridiem,
  PickerValue,
  TimeViewWithMeridiem,
} from '../internals/models';
import type {
  ExportedValidateDateTimeProps,
  ValidateDateTimePropsToDefault,
} from '../validation/validateDateTime';
import { resolveTimeViewsResponse } from '../internals/utils/date-time-utils';
import type { DigitalClockSlotProps, DigitalClockSlots } from '../DigitalClock';
import type {
  MultiSectionDigitalClockSlotProps,
  MultiSectionDigitalClockSlots,
} from '../MultiSectionDigitalClock';
import { useApplyDefaultValuesToDateTimeValidationProps } from '../managers/useDateTimeManager';

export interface BaseDateTimePickerSlots
  extends DateCalendarSlots, DigitalClockSlots, MultiSectionDigitalClockSlots {
  /**
   * Tabs enabling toggling between date and time pickers.
   * @default DateTimePickerTabs
   */
  tabs?: React.ElementType<DateTimePickerTabsProps>;
  /**
   * Custom component for the toolbar rendered above the views.
   * @default DateTimePickerToolbar
   */
  toolbar?: React.JSXElementConstructor<DateTimePickerToolbarProps>;
}

export interface BaseDateTimePickerSlotProps
  extends DateCalendarSlotProps, DigitalClockSlotProps, MultiSectionDigitalClockSlotProps {
  /**
   * Props passed down to the tabs component.
   */
  tabs?: WithDataAttributes<DateTimePickerTabsProps>;
  /**
   * Props passed down to the toolbar component.
   */
  toolbar?: WithDataAttributes<ExportedDateTimePickerToolbarProps>;
}

export type DateTimePickerViewRenderers<TView extends DateOrTimeViewWithMeridiem> =
  PickerViewRendererLookup<
    PickerValue,
    TView,
    Omit<DateViewRendererProps<TView>, 'slots' | 'slotProps'> &
      Omit<
        TimeViewRendererProps<TimeViewWithMeridiem, BaseClockProps<TimeViewWithMeridiem>>,
        'slots' | 'slotProps'
      >
  >;

export interface BaseDateTimePickerProps
  extends
    Omit<
      BasePickerInputProps<PickerValue, DateOrTimeViewWithMeridiem, DateTimeValidationError>,
      'views'
    >,
    Omit<ExportedDateCalendarProps, 'onViewChange' | 'views'>,
    DigitalTimePickerProps,
    ExportedValidateDateTimeProps {
  /**
   * Display ampm controls under the clock (instead of in the toolbar).
   * @default true on desktop, false on mobile
   */
  ampmInClock?: boolean;
  /**
   * Overridable component slots.
   * @default {}
   */
  slots?: BaseDateTimePickerSlots;
  /**
   * The props used for each component slot.
   * @default {}
   */
  slotProps?: BaseDateTimePickerSlotProps;
  /**
   * Define custom view renderers for each section.
   * If `null`, the section will only have field editing.
   * If `undefined`, internally defined view will be used.
   */
  viewRenderers?: Partial<DateTimePickerViewRenderers<DateOrTimeViewWithMeridiem>>;
  /**
   * Available views.
   */
  views?: readonly DateOrTimeView[];
}

type UseDateTimePickerDefaultizedProps<Props extends BaseDateTimePickerProps> = LocalizedComponent<
  Omit<
    DefaultizedProps<Props, 'openTo' | 'orientation' | 'ampm' | ValidateDateTimePropsToDefault>,
    'views'
  >
> & {
  shouldRenderTimeInASingleColumn: boolean;
  views: readonly DateOrTimeViewWithMeridiem[];
  viewsForFormatting: readonly DateOrTimeViewWithMeridiem[];
};

export function useDateTimePickerDefaultizedProps<Props extends BaseDateTimePickerProps>(
  props: Props,
  name: string,
): UseDateTimePickerDefaultizedProps<Props> {
  const adapter = usePickerAdapter();
  const themeProps = useThemeProps({
    props,
    name,
  });

  const validationProps = useApplyDefaultValuesToDateTimeValidationProps(themeProps);
  const ampm = themeProps.ampm ?? adapter.is12HourCycleInCurrentLocale();

  const localeText = React.useMemo<PickersInputLocaleText | undefined>(() => {
    if (themeProps.localeText?.toolbarTitle == null) {
      return themeProps.localeText;
    }

    return {
      ...themeProps.localeText,
      dateTimePickerToolbarTitle: themeProps.localeText.toolbarTitle,
    };
  }, [themeProps.localeText]);

  const { openTo, views: defaultViews } = applyDefaultViewProps<DateOrTimeViewWithMeridiem>({
    views: themeProps.views,
    openTo: themeProps.openTo,
    defaultViews: ['year', 'day', 'hours', 'minutes'],
    defaultOpenTo: 'day',
  });

  const {
    shouldRenderTimeInASingleColumn,
    thresholdToRenderTimeInASingleColumn,
    views,
    timeSteps,
  } = resolveTimeViewsResponse<any, DateOrTimeViewWithMeridiem>({
    thresholdToRenderTimeInASingleColumn: themeProps.thresholdToRenderTimeInASingleColumn,
    ampm,
    timeSteps: themeProps.timeSteps,
    views: defaultViews,
  });

  // Keep the original views for format calculation (before filtering)
  const viewsForFormatting: readonly DateOrTimeViewWithMeridiem[] = ampm
    ? [...defaultViews, 'meridiem']
    : defaultViews;

  return {
    ...themeProps,
    ...validationProps,
    timeSteps,
    openTo,
    shouldRenderTimeInASingleColumn,
    thresholdToRenderTimeInASingleColumn,
    views,
    viewsForFormatting,
    ampm,
    localeText,
    orientation: themeProps.orientation ?? 'portrait',
    slots: {
      toolbar: DateTimePickerToolbar,
      tabs: DateTimePickerTabs,
      ...themeProps.slots,
    },
    slotProps: {
      ...themeProps.slotProps,
      toolbar: {
        ampm,
        ...themeProps.slotProps?.toolbar,
      },
    },
  };
}
